import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  InputNumber, 
  Select, 
  Button, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Space,
  Table,
  Tag,
  Statistic,
  Alert,
  Collapse,
  Progress,
  Rate
} from 'antd';
import { CalculatorOutlined, CloudOutlined, ExperimentOutlined, SafetyOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const BenefitCalculator = () => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const regionOptions = [
    { label: '华南地区（1500-2000 mm）', value: 1750 },
    { label: '华东地区（1200-1600 mm）', value: 1400 },
    { label: '华中地区（1000-1400 mm）', value: 1200 },
    { label: '西南地区（800-1200 mm）', value: 1000 },
    { label: '华北地区（500-800 mm）', value: 650 },
    { label: '西北地区（200-500 mm）', value: 350 },
    { label: '东北地区（400-700 mm）', value: 550 },
  ];

  const surfaceTypeOptions = [
    { label: '裸地（0.60-0.80）', value: 0.70 },
    { label: '荒地（0.50-0.70）', value: 0.60 },
    { label: '耕地（0.30-0.50）', value: 0.40 },
    { label: '草地（0.20-0.40）', value: 0.30 },
    { label: '灌木林地（0.15-0.35）', value: 0.25 },
    { label: '乔木林地（0.10-0.30）', value: 0.20 },
    { label: '混交林地（0.08-0.25）', value: 0.165 },
    { label: '密林地（0.05-0.20）', value: 0.125 },
  ];

  const measureOptions = [
    { 
      label: '梯田（径流削减率40-60%，保土率70-90%）', 
      value: JSON.stringify({ runoffReduction: 0.50, soilRetention: 0.80 }) 
    },
    { 
      label: '等高耕作（径流削减率20-40%，保土率40-60%）', 
      value: JSON.stringify({ runoffReduction: 0.30, soilRetention: 0.50 }) 
    },
    { 
      label: '水土保持林（径流削减率30-50%，保土率60-85%）', 
      value: JSON.stringify({ runoffReduction: 0.40, soilRetention: 0.725 }) 
    },
    { 
      label: '草地恢复（径流削减率25-45%，保土率50-75%）', 
      value: JSON.stringify({ runoffReduction: 0.35, soilRetention: 0.625 }) 
    },
    { 
      label: '封禁治理（径流削减率20-35%，保土率40-60%）', 
      value: JSON.stringify({ runoffReduction: 0.275, soilRetention: 0.50 }) 
    },
    { 
      label: '沉沙池（径流削减率15-30%，保土率50-70%）', 
      value: JSON.stringify({ runoffReduction: 0.225, soilRetention: 0.60 }) 
    },
    { 
      label: '排水沟（径流削减率10-25%，保土率0%）', 
      value: JSON.stringify({ runoffReduction: 0.175, soilRetention: 0.00 }) 
    },
    { 
      label: '谷坊（径流削减率20-35%，保土率55-75%）', 
      value: JSON.stringify({ runoffReduction: 0.275, soilRetention: 0.65 }) 
    },
    { 
      label: '挡土墙（径流削减率10-20%，保土率60-80%）', 
      value: JSON.stringify({ runoffReduction: 0.15, soilRetention: 0.70 }) 
    },
    { 
      label: '工程护坡（径流削减率0%，保土率65-85%）', 
      value: JSON.stringify({ runoffReduction: 0.00, soilRetention: 0.75 }) 
    },
    { 
      label: '植被护坡（径流削减率0%，保土率50-70%）', 
      value: JSON.stringify({ runoffReduction: 0.00, soilRetention: 0.60 }) 
    },
  ];

  const erosionLevelOptions = [
    { label: '微度侵蚀（<200 t/(km²·a)）', value: 100 },
    { label: '轻度侵蚀（200-2500 t/(km²·a)）', value: 1350 },
    { label: '中度侵蚀（2500-5000 t/(km²·a)）', value: 3750 },
    { label: '强烈侵蚀（5000-8000 t/(km²·a)）', value: 6500 },
    { label: '极强烈侵蚀（8000-15000 t/(km²·a)）', value: 11500 },
    { label: '剧烈侵蚀（>15000 t/(km²·a)）', value: 20000 },
  ];

  const calculateBenefit = (values) => {
    const { 
      rainfall, 
      surfaceType, 
      measureType, 
      area, 
      duration,
      erosionLevel
    } = values;

    const measureTypeObj = typeof measureType === 'string' ? JSON.parse(measureType) : measureType;

    const P = rainfall;
    const Q0 = surfaceType;
    const Q1 = Q0 * (1 - measureTypeObj.runoffReduction);
    const M0 = erosionLevel;
    const M1 = M0 * (1 - measureTypeObj.soilRetention);

    const W = (P / 1000) * (1 - Q1 / Q0) * area * 10000;
    const S = (M0 - M1) * area;
    const Rw = (1 - Q1 / Q0) * 100;
    const Rs = (1 - M1 / M0) * 100;

    const WTotal = W * duration;
    const STotal = S * duration;

    return {
      W, S, Rw, Rs,
      WTotal, STotal,
      P, Q0, Q1, M0, M1,
      area, duration
    };
  };

  const getWaterBenefitLevel = (Rw) => {
    if (Rw > 60) {
      return { level: '优', score: 5, color: '#52c41a' };
    } else if (Rw >= 40) {
      return { level: '良', score: 4, color: '#1890ff' };
    } else if (Rw >= 20) {
      return { level: '中', score: 3, color: '#faad14' };
    } else {
      return { level: '差', score: 2, color: '#ff4d4f' };
    }
  };

  const getSoilBenefitLevel = (Rs) => {
    if (Rs > 70) {
      return { level: '优', score: 5, color: '#52c41a' };
    } else if (Rs >= 50) {
      return { level: '良', score: 4, color: '#1890ff' };
    } else if (Rs >= 30) {
      return { level: '中', score: 3, color: '#faad14' };
    } else {
      return { level: '差', score: 2, color: '#ff4d4f' };
    }
  };

  const getOverallBenefitLevel = (waterLevel, soilLevel) => {
    const avgScore = (waterLevel.score + soilLevel.score) / 2;
    if (avgScore >= 4.5) {
      return { level: '优', color: '#52c41a' };
    } else if (avgScore >= 3.5) {
      return { level: '良', color: '#1890ff' };
    } else if (avgScore >= 2.5) {
      return { level: '中', color: '#faad14' };
    } else {
      return { level: '差', color: '#ff4d4f' };
    }
  };

  const getOptimizationSuggestions = (result) => {
    const suggestions = [];

    if (result.Rw < 40) {
      suggestions.push({
        type: 'warning',
        title: '保水效益偏低',
        description: '建议增加径流拦截措施，如梯田、水土保持林等，以提高保水效果'
      });
    }

    if (result.Rs < 50) {
      suggestions.push({
        type: 'warning',
        title: '保土效益偏低',
        description: '建议加强土壤保持措施，如工程护坡、挡土墙等，以提高保土效果'
      });
    }

    if (result.Rw >= 40 && result.Rs >= 50) {
      suggestions.push({
        type: 'success',
        title: '措施效果良好',
        description: '当前措施组合效果良好，建议继续保持并定期监测'
      });
    }

    return suggestions;
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const result = calculateBenefit(values);
      setResult(result);
    } catch (error) {
      console.error('计算失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
  };

  const resultColumns = [
    {
      title: '参数',
      dataIndex: 'parameter',
      key: 'parameter',
      width: '40%',
    },
    {
      title: '数值',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (value) => typeof value === 'number' ? value.toFixed(2) : value,
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: '30%',
    },
  ];

  const resultData = result ? [
    { key: '1', parameter: '年保水量', value: result.W, unit: 'm³/年' },
    { key: '2', parameter: '年保土量', value: result.S, unit: 't/年' },
    { key: '3', parameter: '保水率', value: result.Rw, unit: '%' },
    { key: '4', parameter: '保土率', value: result.Rs, unit: '%' },
    { key: '5', parameter: '实施期内总保水量', value: result.WTotal, unit: 'm³' },
    { key: '6', parameter: '实施期内总保土量', value: result.STotal, unit: 't' },
  ] : [];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
          <CalculatorOutlined /> 水土保持措施效益评估计算器
        </Title>
        <Text type="secondary">
          量化评估不同水土保持措施的保水保土效益
        </Text>
      </div>

      <Alert
        message="计算依据"
        description="本工具依据 GB/T 15774-1995《水土保持综合治理 效益计算方法》、GB 50433-2018《生产建设项目水土保持技术标准》进行计算。"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title="输入参数" variant="borderless" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Collapse defaultActiveKey={['1', '2', '3', '4']}>
                <Panel header={<><CloudOutlined /> 区域信息</>} key="1">
                  <Form.Item
                    label="项目所在地区"
                    name="rainfall"
                    rules={[{ required: true, message: '请选择项目所在地区' }]}
                  >
                    <Select
                      placeholder="请选择项目所在地区"
                      options={regionOptions}
                    />
                  </Form.Item>
                </Panel>

                <Panel header={<><ExperimentOutlined /> 侵蚀背景信息</>} key="2">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="地表类型"
                        name="surfaceType"
                        rules={[{ required: true, message: '请选择地表类型' }]}
                      >
                        <Select
                          placeholder="请选择地表类型"
                          options={surfaceTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="侵蚀强度等级"
                        name="erosionLevel"
                        rules={[{ required: true, message: '请选择侵蚀强度等级' }]}
                      >
                        <Select
                          placeholder="请选择侵蚀强度等级"
                          options={erosionLevelOptions}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><SafetyOutlined /> 措施信息</>} key="3">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="措施类型"
                        name="measureType"
                        rules={[{ required: true, message: '请选择措施类型' }]}
                      >
                        <Select
                          placeholder="请选择措施类型"
                          options={measureOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="措施实施面积（hm²）"
                        name="area"
                        rules={[{ required: true, message: '请输入措施实施面积' }]}
                      >
                        <InputNumber
                          min={0.1}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入措施实施面积"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="措施实施时间（年）"
                        name="duration"
                        rules={[{ required: true, message: '请输入措施实施时间' }]}
                        initialValue={5}
                      >
                        <InputNumber
                          min={1}
                          precision={1}
                          style={{ width: '100%' }}
                          placeholder="请输入措施实施时间"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<CalculatorOutlined />} 
                  onClick={handleCalculate}
                  loading={loading}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  开始计算
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {result && (
            <>
              <Card title="计算结果" variant="borderless" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="年保水量"
                      value={result.W.toFixed(2)}
                      suffix="m³/年"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="年保土量"
                      value={result.S.toFixed(2)}
                      suffix="t/年"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="保水率"
                      value={result.Rw.toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="保土率"
                      value={result.Rs.toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                </Row>

                <Divider />

                <div style={{ marginBottom: '16px' }}>
                  <Text strong>效益等级评价：</Text>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">保水效益</Text>
                      </div>
                      <Tag 
                        color={getWaterBenefitLevel(result.Rw).color} 
                        style={{ fontSize: '16px', padding: '8px 16px' }}
                      >
                        {getWaterBenefitLevel(result.Rw).level}
                      </Tag>
                      <div style={{ marginTop: '8px' }}>
                        <Rate disabled count={5} value={getWaterBenefitLevel(result.Rw).score} />
                      </div>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">保土效益</Text>
                      </div>
                      <Tag 
                        color={getSoilBenefitLevel(result.Rs).color} 
                        style={{ fontSize: '16px', padding: '8px 16px' }}
                      >
                        {getSoilBenefitLevel(result.Rs).level}
                      </Tag>
                      <div style={{ marginTop: '8px' }}>
                        <Rate disabled count={5} value={getSoilBenefitLevel(result.Rs).score} />
                      </div>
                    </div>
                  </Col>
                </Row>

                <Divider />

                <div style={{ marginBottom: '12px' }}>
                  <Text strong>综合效益：</Text>
                  <Tag 
                    color={getOverallBenefitLevel(getWaterBenefitLevel(result.Rw), getSoilBenefitLevel(result.Rs)).color} 
                    style={{ fontSize: '18px', padding: '10px 20px', marginLeft: '12px' }}
                  >
                    {getOverallBenefitLevel(getWaterBenefitLevel(result.Rw), getSoilBenefitLevel(result.Rs)).level}
                  </Tag>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary">保水率：</Text>
                  <Progress percent={Math.min(result.Rw, 100)} status={result.Rw >= 60 ? 'success' : result.Rw >= 40 ? 'normal' : 'exception'} />
                </div>
                <div>
                  <Text type="secondary">保土率：</Text>
                  <Progress percent={Math.min(result.Rs, 100)} status={result.Rs >= 70 ? 'success' : result.Rs >= 50 ? 'normal' : 'exception'} />
                </div>
              </Card>

              <Card title="详细参数表" variant="borderless" style={{ marginBottom: '24px' }}>
                <Table
                  columns={resultColumns}
                  dataSource={resultData}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Card>

              <Card title="优化建议" variant="borderless">
                {getOptimizationSuggestions(result).map((suggestion, index) => (
                  <Alert
                    key={index}
                    message={suggestion.title}
                    description={suggestion.description}
                    type={suggestion.type}
                    showIcon
                    style={{ marginBottom: index < getOptimizationSuggestions(result).length - 1 ? '12px' : 0 }}
                  />
                ))}
              </Card>
            </>
          )}

          {!result && (
            <Card variant="borderless">
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <CalculatorOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">请输入参数并点击"开始计算"按钮</Text>
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BenefitCalculator;
